// sim/territoryData.ts — the ESTABLISHMENT LADDER: per-stage tuning for how a
// reached region is marched from a freshly-scouted frontier into a fully
// integrated province. Pure data, no logic (the TerritorySystem in territory.ts
// reads it). Every array is indexed positionally by the TERRITORY_STAGE constant
// (the stage you are advancing FROM) — that order is a hard contract.
// Grounded in docs/LORE.md §"Regions — the expanding world": new territory is
// not instantly useful; the colony must invest worker labor before a zone pays
// its way, and only a fully-absorbed province yields a bonus over baseline.

import {
  TERRITORY_STAGE,
  TERRITORY_STAGE_COUNT,
  ESTABLISH_WORK,
  ESTABLISH_WORK_COUNT,
} from '../types.ts';

/**
 * Which ESTABLISH_WORK channel each stage consumes to advance to the next stage.
 * Indices 0 (Unexplored) and 5 (Integrated) are unused sentinels — never read
 * for advancement (a region only advances while in [Explored, Established]).
 */
export const STAGE_WORK: readonly number[] = [
  /* Unexplored  */ 0,
  /* Explored    */ ESTABLISH_WORK.Trail,
  /* Claimed     */ ESTABLISH_WORK.Logistics,
  /* Settled     */ ESTABLISH_WORK.FoodTransport,
  /* Established  */ ESTABLISH_WORK.RouteReinforce,
  /* Integrated  */ 0,
];

/**
 * Effort (accumulated in that stage's work channel) required to LEAVE the stage.
 * Rising costs make later stages a longer haul. Unexplored & Integrated are
 * 0/unused — neither is a stage the colony actively works through.
 *
 * Tuning rationale (kept — these round numbers are defensible against the real
 * colony growth curve, NOT arbitrary; see territory.ts LABOR_DIVISOR):
 *   • A starter colony (~10 workers+specialists) banks `1 + floor(10/4) = 3`
 *     effort/step, so the four rungs are ~7 / 14 / 24 / 37 steps of *sustained*
 *     labor — and because each rung reads a fresh (empty) channel, the colony
 *     stalls one step between rungs (the intended "march outward" gap). The full
 *     240-effort arc is therefore a long haul a fledgling cannot rush, while a
 *     mature colony (40+ labor ⇒ 11/step) integrates a zone in a few dozen steps.
 *   • The curve is strictly increasing (20 < 40 < 70 < 110) with widening gaps
 *     (+20 / +30 / +40), so later provinces cost progressively more to absorb —
 *     matching the lore's "only a fully-absorbed province yields a bonus."
 *   • The 20 floor is small enough that the FIRST foothold (Explored→Claimed)
 *     lands early (the colony sees progress quickly), which the integration test
 *     in territory.test.mjs depends on.
 * Re-derive against a growth curve before changing any entry; the tests pin the
 * arithmetic, so a bump must be justified + the tests updated in lock-step.
 */
export const STAGE_REQUIREMENT: readonly number[] = [
  /* Unexplored  */ 0,
  /* Explored    */ 20,
  /* Claimed     */ 40,
  /* Settled     */ 70,
  /* Established  */ 110,
  /* Integrated  */ 0,
];

/**
 * Percent of a region's base resource amount actually delivered home at each
 * stage. A scouted-but-unworked zone yields nothing; a fully Integrated province
 * over-delivers (130%) thanks to hardened supply routes.
 */
export const STAGE_YIELD_PCT: readonly number[] = [
  /* Unexplored  */ 0,
  /* Explored    */ 0,
  /* Claimed     */ 35,
  /* Settled     */ 70,
  /* Established  */ 100,
  /* Integrated  */ 130,
];

/** 1 if a region's gene drops are accessible at this stage, else 0 (off until Settled). */
export const STAGE_GENE_ACCESS: readonly number[] = [
  /* Unexplored  */ 0,
  /* Explored    */ 0,
  /* Claimed     */ 0,
  /* Settled     */ 1,
  /* Established  */ 1,
  /* Integrated  */ 1,
];

/**
 * Delta added to a region's rarityBias at this stage. Early footholds are noisy
 * and skew toward common drops (negative); a settled province eventually edges
 * the bias upward (+1) as the colony works the richest seams.
 */
export const STAGE_RARITY_DELTA: readonly number[] = [
  /* Unexplored  */ 0,
  /* Explored    */ -2,
  /* Claimed     */ -1,
  /* Settled     */ -1,
  /* Established  */ 0,
  /* Integrated  */ 1,
];

// --- Abandonment decay (OPT-IN; default OFF ⇒ byte-identical) ----------------

/**
 * Tuning for SLOW EFFORT DECAY / STAGE DEMOTION of a NEGLECTED region — the
 * "establishment can't regress" backlog item (docs/BACKLOG.md §Territorial
 * establishment). A region that stops receiving labor/throughput (colony pulled
 * out, war lost) bleeds banked effort and can slip back DOWN the ladder toward
 * Explored. Read by the TerritorySystem; pure data, no logic.
 *
 * DEFAULT IS INERT: `abandonDecay = 0` disables the whole mechanism, so a
 * TerritorySystem built with no config (or `DEFAULT_TERRITORY_DECAY`) behaves
 * EXACTLY as before — same advancement, same hash. Enable by passing a config
 * with `abandonDecay > 0`.
 */
export interface TerritoryDecayConfig {
  /**
   * Effort drained from the region's CURRENT-stage channel each time the decay
   * timer fires while the region is idle. 0 (the default) disables decay
   * entirely — no idle tracking, no demotion, byte-identical to the no-decay
   * system. Must be ≥ 0 (clamped).
   */
  readonly abandonDecay: number;
  /**
   * How many establishment steps between decay ticks. Decay is GATED on a step
   * counter (not applied every call) so it is a slow bleed, not an instant
   * collapse. Must be ≥ 1 (clamped); ignored when `abandonDecay === 0`.
   */
  readonly decayInterval: number;
  /**
   * Idle GRACE: a region must miss this many consecutive establishment steps
   * (steps in which it received zero effort) before decay begins. Any positive
   * effort resets the idle clock, so a region under active labor never decays.
   * Must be ≥ 0 (clamped).
   */
  readonly idleGraceSteps: number;
  /**
   * The lowest stage a neglected region can be demoted TO. Demotion never falls
   * below this floor however long the region is abandoned. Defaults to Explored
   * (the bottom of the ladder) — a region the frontier reached is never
   * un-explored. Clamped into [Explored, Established].
   */
  readonly demoteFloor: number;
  /**
   * HYSTERESIS guard against boundary thrash. After a demotion, the freshly
   * re-entered (lower) stage's channel is refilled to a FRACTION of its
   * requirement — `floor(requirement * demoteHoldPct / 100)` — rather than left
   * empty. So a region that just slipped a rung sits comfortably MID-stage, not
   * one effort point from re-promoting (which would otherwise let a single
   * stray labor step bounce it straight back up and re-demote next interval).
   * Clamped into [0, 99]. 0 ⇒ demote to an empty channel (max hysteresis: the
   * region must re-earn the whole rung to climb again).
   */
  readonly demoteHoldPct: number;
}

/** Decay OFF — the safe default. A system built with this is byte-identical to no decay. */
export const DEFAULT_TERRITORY_DECAY: TerritoryDecayConfig = {
  abandonDecay: 0,
  decayInterval: 1,
  idleGraceSteps: 0,
  demoteFloor: TERRITORY_STAGE.Explored,
  demoteHoldPct: 0,
};

/**
 * A reasonable ENABLED decay preset for callers that want regression without
 * hand-tuning. Tuned to be MUCH slower than advancement so abandonment reads as
 * a gradual slip, never a cliff: a region under labor climbs a rung in ~7–37
 * steps (see STAGE_REQUIREMENT), whereas an abandoned one only starts bleeding
 * after `idleGraceSteps` of total silence and then loses `abandonDecay` effort
 * every `decayInterval` steps — so a full Established (110 effort) zone takes
 * 32 grace + ~28 decay ticks × interval to even begin shedding a stage. Demotes
 * no lower than Explored; refills 50% on demotion so a boundary can't thrash.
 */
export const SLOW_TERRITORY_DECAY: TerritoryDecayConfig = {
  abandonDecay: 1,
  decayInterval: 4,
  idleGraceSteps: 32,
  demoteFloor: TERRITORY_STAGE.Explored,
  demoteHoldPct: 50,
};

// --- Length / contract assertions (evaluated at import — catch mis-edits). ---
const _assertTerritoryData = (() => {
  const arrays: readonly (readonly number[])[] = [
    STAGE_WORK,
    STAGE_REQUIREMENT,
    STAGE_YIELD_PCT,
    STAGE_GENE_ACCESS,
    STAGE_RARITY_DELTA,
  ];
  for (const arr of arrays) {
    if (arr.length !== TERRITORY_STAGE_COUNT) {
      throw new Error(
        `territory stage array length ${arr.length} !== TERRITORY_STAGE_COUNT ${TERRITORY_STAGE_COUNT}`,
      );
    }
  }
  // Every active stage's work channel must be a valid ESTABLISH_WORK index.
  for (let s = TERRITORY_STAGE.Explored; s <= TERRITORY_STAGE.Established; s++) {
    const w = STAGE_WORK[s] as number;
    if (w < 0 || w >= ESTABLISH_WORK_COUNT) {
      throw new Error(`STAGE_WORK[${s}] is ${w}, must be in [0, ${ESTABLISH_WORK_COUNT})`);
    }
  }
  // The shipped decay presets must obey the documented bounds (catch a mis-edit).
  for (const [label, cfg] of [
    ['DEFAULT_TERRITORY_DECAY', DEFAULT_TERRITORY_DECAY],
    ['SLOW_TERRITORY_DECAY', SLOW_TERRITORY_DECAY],
  ] as const) {
    if (cfg.abandonDecay < 0) throw new Error(`${label}.abandonDecay must be ≥ 0`);
    if (cfg.decayInterval < 1) throw new Error(`${label}.decayInterval must be ≥ 1`);
    if (cfg.idleGraceSteps < 0) throw new Error(`${label}.idleGraceSteps must be ≥ 0`);
    if (cfg.demoteFloor < TERRITORY_STAGE.Explored || cfg.demoteFloor > TERRITORY_STAGE.Established) {
      throw new Error(`${label}.demoteFloor must be in [Explored, Established]`);
    }
    if (cfg.demoteHoldPct < 0 || cfg.demoteHoldPct > 99) {
      throw new Error(`${label}.demoteHoldPct must be in [0, 99]`);
    }
  }
})();
